import {useState} from 'react';
import {RefreshCw} from 'lucide-react';
import {
  Button,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {ReportParameters, parameterDefaults} from './ReportParameters';
import {type ReportParameter, type ReportQuery, type ReportSummary} from '@/api/reporting';

// The title, its Re-run report button and the parameter inputs. The parameters as edited but not yet
// run are held here as a draft; the URL holds what was last run, so the draft is seeded from it (over
// the report's defaults, which the service applies to anything the URL leaves out) and the page
// re-keys this whenever the report or the run parameters change underneath.
export function ReportControls({
  summary,
  parameters,
  applied,
  running,
  autoRun = false,
  onRun
}: {
  readonly summary: ReportSummary;
  // The report's parameters as the last response gave them, with each select's choices; the list's
  // own do not carry those. Until a report has loaded they are the list's.
  readonly parameters: readonly ReportParameter[];
  readonly applied: ReportQuery;
  readonly running: boolean;
  // Apply every edit as it is made, with no Re-run report button: the page follows its filters.
  readonly autoRun?: boolean;
  readonly onRun: (next: ReportQuery) => void;
}) {
  const [draft, setDraft] = useState<ReportQuery>(() => ({
    ...parameterDefaults(parameters),
    ...applied
  }));
  // Edits not yet run: the draft against what was last run (the URL over the defaults), so the
  // analyst is told the rows on screen no longer match the filters.
  const lastRun = {...parameterDefaults(parameters), ...applied};
  const stale = [...Object.keys(draft), ...Object.keys(lastRun)].some(
    (name) => (draft[name] ?? '') !== (lastRun[name] ?? '')
  );
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-medium tracking-tight">{summary.title}</h2>
          {summary.description ? (
            <p className="text-muted-foreground text-sm">{summary.description}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {autoRun ? (
            <p
              role="status"
              aria-live="polite"
              className="text-muted-foreground flex items-center gap-2 text-xs">
              {running ? <Spinner /> : null}
              {running ? 'Updating…' : null}
            </p>
          ) : null}
          {!autoRun && stale ? (
            <p role="status" className="text-muted-foreground text-xs">
              Filters changed. Re-run the report to apply them.
            </p>
          ) : null}
          {autoRun ? null : (
            <Button
              type="button"
              variant={stale ? 'default' : 'outline'}
              size="sm"
              disabled={running}
              onClick={() => {
                onRun(draft);
              }}>
              {running ? <Spinner /> : <RefreshCw />}
              Re-run report
            </Button>
          )}
        </div>
      </div>
      <ReportParameters
        parameters={parameters}
        values={draft}
        onChange={(next) => {
          // A toggle switches the view rather than refining it, so it runs at once (its other
          // options are prefetched); every other edit waits for Re-run report.
          const switched = parameters.some(
            (parameter) =>
              parameter.type === 'toggle' && next[parameter.name] !== draft[parameter.name]
          );
          setDraft(next);
          if (switched || autoRun) {
            onRun(next);
          }
        }}
      />
    </>
  );
}

// A wait the page cannot show anything for yet: the design system's empty state, with the spinner
// as its media.
export function LoadingState({title}: {readonly title: string}) {
  return (
    <Empty className="py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Spinner />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
      </EmptyHeader>
    </Empty>
  );
}
